import { colors, getBreakPoint, useScreenWidth } from "oolib";
import React, { Fragment, useContext } from "react";
import { useNavigate } from 'react-router-dom';

import { queryClient } from "../../..";
import { AppSettingsContext } from "../../../contexts/appSettingsContext";
import { trackEventGA } from "../../../trackers/GA/trackEventGA";
import {
  __CheckAccessToContentType,
  __IsAuthor,
} from "../../../utils/accessControl";

import { useModal } from "../../../utils/customHooks";

import { __GetContentTypeConfigNew } from "../../../utils/getters/gettersV2";
import { useGetQueryData } from "../../../utils/react-query-hooks/general";

import KPShareActions from "../../share/KPShareActions";

import styled from "styled-components";
import { CommentsButton } from "../../../pageTpls/PublishedPage/comps/ContentEngagementButtonPanel/Comps/CommentsBtn";
import { ReactionBtn } from "../../../pageTpls/PublishedPage/comps/ContentEngagementButtonPanel/Comps/ReactionBtn";
import { ShareBtn } from "../../../pageTpls/PublishedPage/comps/ContentEngagementButtonPanel/Comps/ShareBtn";
import useShareContent from "../../../utils/react-query-hooks/ShareUrl/useShareContent";
import { DeleteModal } from "./Comps/DeleteModal";
import { TPLActionMenu } from "./Comps/TplActionMenu";
import { StyledDivider } from "../../../pageTpls/PublishedPage/comps/ContentEngagementButtonPanel/Comps/styled";
import { useDeleteContent } from "../../../pageTpls/ContributeTpl/queryHooks/useDeleteContent";
// import { useCheckIfPblDocHasBeenEditedAndResubmitted } from "../../../pageTpls/ContributeTpl/utils/useCheckIfPblDocHasBeenEditedAndResubmitted";
import { useDeleteUnderModeration } from "../../../utils/react-query-hooks/moderation";
import { getRoutePathToModerateContent, getRoutePathToEditContent } from "@okf/ootils";
import { getPublishedDocState } from "../../../utils/getPublishedDocState";

const StyledPaddingLeftRight10 = styled.div`
padding: 0 1rem;
`;

const StyledPaddingLeft10 = styled.div`
padding-left: 1rem;
`;


const TplActions = ({
  content,
  invert,
  kebabStyle,
  popOutOfOverflowHiddenParent,
  tplActionsIcon,
  buttonSecondary,
  // self-serve passes inline action overrides { onEdit, onDelete }. The EXISTING
  // edit/delete buttons + access gating are kept; we only swap their navigating
  // handlers for inline ones so the participant stays on /self-serve-survey.
  selfServeActions,
  // render the actions as inline compact buttons (PublishedPageV3 header) instead
  // of the kebab menu, and drop the share/comment buttons.
  publishedPageV3Actions,
  // collab-edited doc: the clone-based Edit/Review flows are rejected
  // server-side (rejectLegacyLifecycleOnCollabTenants) — hide their buttons.
  // Delete stays.
  suppressLegacyLifecycleActions,
}) => {
  const { APP_SETTINGS } = useContext(AppSettingsContext);

  //react-query-hook

  const { mutate: deleteVia_deleteContentAPI } = useDeleteContent();
  const {mutate:deleteVia_deleteByModeratorAPI} = useDeleteUnderModeration()

  const [showDeleteModal, setShowDeleteModal] = useModal(false);

  const {
    meta: { kp_content_type: contentType, kp_contributed_by },
    _id: contentId,
    kp_published_status: contentPublishedStatus,
    currentEditor,
    reactions: reactionsOnContent,
    comments: _comments,
  } = content;

  const {
    SELF_MANAGED_BASE_CONFIGS
  } = useGetQueryData("platformConfigs");

  

  // const {screenWidth} = useSelector(s => s.environment)
  const screenWidth = useScreenWidth();

  const userData = useGetQueryData("userData");

  const { handleShareButtonClick, shortUrl, setShortUrl, genShareUrl } =
    useShareContent(contentId, contentType, content);

  const {  latestContent } = queryClient.getQueryData([
    "Contributions",
    "view",
    contentType,
    contentId,
    APP_SETTINGS.lang?.value, // TODOs_kishan fix it.
  ]) || {};

  // shared with PublishedPageV3's annotation gating — single definition of the
  // doc's current status / clean-published-ness.
  const { currentStatus } = getPublishedDocState(latestContent)

  const contentTypeConfig = __GetContentTypeConfigNew(contentType);
  const { sharing, reactions, comments } = contentTypeConfig?.general || {};

  const navigate = useNavigate();

  const handleEditPublished = () => {
    trackEventGA(
      `Published Content`,
      `Clicked Edit On Published Content`,
      `${contentType}`
    );

    // self-serve: edit inline instead of navigating to the edit route
    if (selfServeActions?.onEdit) return selfServeActions.onEdit();

    if (currentStatus === "editPublished") {
      navigate(
        getRoutePathToEditContent({
          contentType, 
          contentId: latestContent.kp_og_published_doc,
          SELF_MANAGED_BASE_CONFIGS
        })
        // `/edit/${contentType}?id=${latestContent.kp_og_published_doc}`
      );
    } else {
      //sent back || draft
      // check getContentForEdit api for the sentback case
      navigate(
        getRoutePathToEditContent({
          contentType, 
          contentId: latestContent._id,
          SELF_MANAGED_BASE_CONFIGS
        })
      )
      // navigate(`/edit/${contentType}?id=${latestContent._id}`);
    }
  };

  const handleModeratePublished = (d) => {
    if (currentStatus === "underModeration") {
      navigate(
        getRoutePathToModerateContent({contentType, contentId: latestContent.kp_pre_mod_doc, SELF_MANAGED_BASE_CONFIGS})
      );
    } else {
      //published or awaitingMod
      navigate(
        getRoutePathToModerateContent({contentType, contentId: latestContent._id, SELF_MANAGED_BASE_CONFIGS})
      );
    }
  };

  //------ checks to show actions -------- //



  const isCurrentEditor = () =>
    typeof latestContent.currentEditor === "string"
      ? latestContent.currentEditor === userData?.user._id
      : latestContent.currentEditor?._id === userData?.user._id;
      

  const isAuthor= ()=>__IsAuthor(kp_contributed_by?._id,userData?.user._id)  // the kp_contributed_by could be null because the user does not exist in "THIS" env.
  

  const canAccessAsAuthor = (ACTION) => {
    if (!kp_contributed_by) return false; // if kp_contributed_by is undefined, and userData.user is undefined, then it will end up returning true for can access as author.
    let authorId = kp_contributed_by?._id;
    let user = userData?.user;
    let userId = userData?.user?._id;

    let canAccess = __CheckAccessToContentType(user, ACTION, contentType);
    let isAuthor = __IsAuthor(authorId, userId);
    return isAuthor && canAccess;
  };

  const isCurrentModerator = () => {
    return __IsAuthor(latestContent.kp_moderator, userData?.user?._id)

  }

  

  const canUpdate = () => {
    let user = userData?.user;
    let canAccess = __CheckAccessToContentType(user, "UPDATE", contentType);
    return canAccess;
  };

  const canDelete = () => {
    let user = userData?.user;
    let canAccess = __CheckAccessToContentType(user, "DELETE", contentType);
    return canAccess;
  };

  const isTheseSegments = ({ segments }) => {
    return (
      segments.indexOf(
        __GetContentTypeConfigNew(contentType)?.general?.segment
      ) !== -1
    );
  };







  // check whether an action is even possible 
  const userHasAccessRightsToEdit = isTheseSegments({
    segments: ["staticPages", "collections"],
  })
    ? canUpdate()
    : canAccessAsAuthor("UPDATE");
   
  const userHasAccessRightsToDelete = isTheseSegments({
    segments: ["staticPages", "collections"],
  })
    ? canDelete()
    : canAccessAsAuthor("DELETE");
  const modHasAccessRightsToReview = userHasAccessRightsToEdit
    ? false
    :!isAuthor() && __CheckAccessToContentType(userData?.user, "MODERATE", contentType);
  const modHasAccessRightsToDelete = modHasAccessRightsToReview;


  

   




  // check whether the action button should be enabled
  const enableEditButton =
    (isTheseSegments({ segments: ["staticPages", "collections"] })
      ? currentStatus === "editPublished"
        ? isCurrentEditor()
        : currentStatus === "published"
        ? canUpdate()
        : false
      : currentStatus === "published" ||
        currentStatus === "editPublished" ||
        currentStatus === "sentBack"
      ? userHasAccessRightsToEdit
      : false);

  const enableModButton =
    isTheseSegments({ segments: ["staticPages", "collections"] })
      ? false
      : currentStatus === "underModeration"
      ? isCurrentModerator()
      : currentStatus === "awaitingModeration" || currentStatus === "published"
      ? modHasAccessRightsToReview
      : false;

  const enableDeleteButton =
    isTheseSegments({ segments: ["staticPages", "collections"] })
      ? currentStatus === "editPublished"
        ? isCurrentEditor()
        : currentStatus === "published"
        ? canDelete()
        : false
      //else we are talking about the publishing segment which has moderation
      : currentStatus === 'published'
        // if doc is published status then either delete access as author or moderate access is required
        ? canAccessAsAuthor('DELETE') || __CheckAccessToContentType(userData?.user, "MODERATE", contentType)
        : ["editPublished", "sentBack", "draft"].includes(currentStatus)
          // editpublished and sentback can only be deleted by the doc's author
          ? canAccessAsAuthor("DELETE")
          // for awaiting moderation, only a moderator can delete
          : currentStatus === 'awaitingModeration'
            ? __CheckAccessToContentType(userData?.user, "MODERATE", contentType)
            // else if current status is undermod, then only current mod can delete
            : currentStatus === "underModeration"
              ? isCurrentModerator()
              // else.. well i dont know if any other statuses are left, but for them jsut default to false
              : false;


      



  const ShowShareButton = sharing?.enable; //content type config flag
  const ShowReactionButton = reactions?.like?.enable && !!userData?.user; //content type config flag
  const ShowCommentsButton = comments?.enable && !!userData?.user;

      
  //meaning its not a kebabStyle && its greater than mobile width, which means only delete button will be there in the action menu
  const TplActionsMenu = (
    <TPLActionMenu
      {...{
        contentType,
        genShareUrl,
        kebabStyle,
        invert,
        popOutOfOverflowHiddenParent,
        // compact-buttons style drops share + comment for now
        ShowShareButton: publishedPageV3Actions ? false : ShowShareButton,
        enableModButton: suppressLegacyLifecycleActions ? false : enableModButton,
        enableEditButton: suppressLegacyLifecycleActions ? false : enableEditButton,
        ShowCommentsButton: publishedPageV3Actions ? false : ShowCommentsButton,
        enableDeleteButton,
        handleShareButtonClick,
        handleModeratePublished,
        handleEditPublished,
        setShowDeleteModal,
        currentStatus,
        userHasAccessRightsToDelete,
        // access-rights flags control button VISIBILITY in the menu (enable*
        // only disables) — zero the edit/review ones under collab, but leave
        // the delete rights (modHasAccessRightsToDelete) untouched.
        userHasAccessRightsToEdit: suppressLegacyLifecycleActions ? false : userHasAccessRightsToEdit,
        modHasAccessRightsToDelete,
        modHasAccessRightsToReview: suppressLegacyLifecycleActions ? false : modHasAccessRightsToReview,
        tplActionsIcon,
        buttonSecondary,
        publishedPageV3Actions,
      }}
    />
  );
  // const typesOfReaction = Object.fromEntries( Object.entries(reactions).filter(([key, value]) => value.enable !== undefined) );
  
  const renderAry = (kebabStyle || publishedPageV3Actions)
    ? [TplActionsMenu]
    : [
        ...(ShowReactionButton ? [
          <StyledPaddingLeftRight10>
            <ReactionBtn
              {...{
                invert,
                textVariant: "short",
                popOutOfOverflowHiddenParent
              }}
            />
          </StyledPaddingLeftRight10>
        ] : []),
        ...(ShowCommentsButton && screenWidth > getBreakPoint("md")
          ? [
              <StyledPaddingLeftRight10>
                <CommentsButton {...{ _comments, invert, textVariant: "short" }} />
              </StyledPaddingLeftRight10>,
            ]
          : []),
        ...(ShowShareButton && ShareBtn && screenWidth > getBreakPoint("md")
          ? [
              <StyledPaddingLeftRight10>
                <ShareBtn
                  {...{ handleShareButtonClick, genShareUrl, invert, showText: false, sharesCount: sharing?.trackShareCount ? content?.shares?.count : undefined }} />
              </StyledPaddingLeftRight10>,
            ]
          : []),
        ...(TPLActionMenu ? [<StyledPaddingLeft10>{TplActionsMenu}</StyledPaddingLeft10>] : []),
      ];

  

  return (
    <Fragment>
      <div style={{ display: "flex", alignItems: "center" }}>
        {shortUrl && <KPShareActions {...{ shortUrl, setShortUrl }} />}

        {showDeleteModal && (
          <DeleteModal
            {...{
              setShowDeleteModal,
              contentType,
              contentId:
                modHasAccessRightsToDelete && enableDeleteButton
                  ? latestContent?._id
                  : contentId,
              mutate: modHasAccessRightsToDelete && enableDeleteButton
                ? (currentStatus === 'underModeration' ? deleteVia_deleteByModeratorAPI : deleteVia_deleteContentAPI)
                : deleteVia_deleteContentAPI,
              SELF_MANAGED_BASE_CONFIGS,
              // self-serve: stay on /self-serve-survey after delete (no nav)
              onDeleteSuccess: selfServeActions?.onAfterSuccess,
            }}
          />
        )}

        <div className="TplMetaBlock__actions">
          {renderAry.map((d, i) => (
            <Fragment>
              {d}
              {i !== renderAry.length - 1 && (
                <StyledDivider
                  style={{ backgroundColor: colors.greyColor80 }}
                />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </Fragment>
  );
};

export default TplActions;
